import { Box, Flex, Image, Shimmer, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import { toggleAddonChoice } from "areas/product/store/actions"
import { to_currency } from "helpers/number"
import { filterProductOptionsV2 } from "helpers/product/product-filtering"

const LoadingContainer = styled(Flex).attrs((props) => ({
  alignItems: "center",
  borderBottom: `1px solid ${props.theme.colors.grey[5]}`,
  pl: 3,
  pr: "6px",
  py: "4px",
}))``

const HiddenInput = styled.input.attrs({ type: "checkbox" })`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

const AddonLabel = styled.label`
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[5]};
  cursor: pointer;
  display: flex;
  padding: 0 14px;
  &:first-child {
    border-top: 1px solid ${(props) => props.theme.colors.grey[5]};
  }
  &:focus-within {
    outline: 2px solid ${(props) => props.theme.colors.primary};
    outline-offset: 0;
  }
  ${(props) => props.theme.mediaQueries.desktop`
    border-top: none;
    padding: 0 6px;
    &:first-child {
      border-top: none;
    }
  `}
`

const StyledCheck = styled(Box)`
  height: 18px;
  margin-right: 6px;
  position: relative;
  width: 18px;
  &:before {
    border: solid ${(props) => props.theme.colors.white};
    border-width: 0 2px 2px 0;
    content: "";
    display: ${(props) => (props.checked ? "block" : "none")};
    height: 9px;
    left: 6px;
    position: absolute;
    top: 3px;
    transform: rotate(45deg);
    width: 5px;
    z-index: 2;
  }
  &:after {
    background: ${(props) =>
      props.checked ? props.theme.colors.black : props.theme.colors.white};
    border: 1px solid ${(props) => props.theme.colors.black};
    border-radius: 50%;
    content: "";
    height: 17px;
    position: absolute;
    width: 17px;
    z-index: 1;
  }
`

const AddonContainer = styled(Box).attrs((props) => ({
  bg: "white",
  borderRadius: { _: "12px", desktop: 0 },
  borderTop: { desktop: `1px solid ${props.theme.colors.grey[5]}` },
  mt: { _: "10px", desktop: 0 },
  mx: { _: 2, desktop: 0 },
  pb: { _: 2, desktop: 0 },
  pt: { _: 2, desktop: "12px" },
}))``

const ProductAddons = ({
  addons,
  adminMode,
  header,
  lid,
  loading,
  prefStore,
  selectedChoices,
  toggleAddon,
  totalItemPrice,
  wizardLoading,
}) => {
  if (
    !addons?.length ||
    wizardLoading ||
    header.params_olid_present ||
    lid ||
    adminMode
  )
    return null

  return (
    <>
      <AddonContainer aria-labelledby="product-addons-heading" role="group">
        <Typography
          as="h2"
          fontSize="14px"
          fontWeight="700"
          id="product-addons-heading"
          mb={{ _: 1, desktop: "2px" }}
          ml={{ _: "14px", desktop: 0 }}
        >
          ADD AN EXTENDER
        </Typography>
        {addons.map((addon) => {
          const selected = selectedChoices.includes(addon.style_code)

          return (
            <div key={`addon-${addon.id}`}>
              {!prefStore ? (
                <LoadingContainer>
                  <Shimmer height="112px" mx={1} width="120px" />
                  <Box>
                    <Shimmer height="14px" mb={1} width="240px" />
                    <Shimmer height="14px" width="40px" />
                  </Box>
                </LoadingContainer>
              ) : (
                <AddonLabel
                  aria-label={addon.display_name}
                  htmlFor={`product-addon-${addon.id}`}
                >
                  <HiddenInput
                    aria-checked={selected}
                    checked={selected}
                    id={`product-addon-${addon.id}`}
                    onChange={() => toggleAddon(addon.style_code)}
                  />
                  <StyledCheck aria-hidden checked={selected} />
                  <Image
                    alt={addon.display_name}
                    height="120"
                    mx={1}
                    width="120"
                    {...(addon.custom_image
                      ? {
                          src: addon.custom_image,
                        }
                      : {
                          fallback: addon.images[0],
                          srcSet: addon.images[1],
                        })}
                  />
                  <Box fontSize="14px" fontWeight="700" lineHeight="14px">
                    <Typography as="h3" fontSize="14px" mb={1}>
                      {addon.display_name}
                    </Typography>
                    <Typography>{to_currency(addon.surcharge)}</Typography>
                  </Box>
                </AddonLabel>
              )}
            </div>
          )
        })}
      </AddonContainer>
      <Flex
        alignItems="center"
        fontSize="18px"
        fontWeight="700"
        justifyContent="space-between"
        lineHeight="20px"
        mb={{ _: "-6px", desktop: 2 }}
        mt={{ _: "10px", desktop: "12px" }}
        px={{ _: 2, desktop: 0 }}
      >
        <Typography as="h2" fontSize="18px">
          Subtotal
        </Typography>
        {loading ? (
          <Shimmer height="20px" width="45px" />
        ) : (
          <Typography textAlign="right">
            {to_currency(totalItemPrice)}
          </Typography>
        )}
      </Flex>
    </>
  )
}

ProductAddons.propTypes = {
  addons: PropTypes.array,
  adminMode: PropTypes.bool,
  header: PropTypes.object,
  lid: PropTypes.string,
  loading: PropTypes.bool,
  prefStore: PropTypes.bool,
  selectedChoices: PropTypes.array,
  toggleAddon: PropTypes.func,
  totalItemPrice: PropTypes.number,
  wizardLoading: PropTypes.string,
}

const mapStateToProps = (state) => {
  const choices = state.choices || {}
  const addons = state.wizard.addons
    ? filterProductOptionsV2(state.wizard.addons, choices, true)
    : []
  const selectedChoices = state.choices.addon ? state.choices.addon.value : []

  const productPrice = state.wizardUI.currentPricing.price_to_display
  const boxPrice =
    state.choices.box && state.choices.box.price ? state.choices.box.price : 0
  const addonPrice = addons
    .filter((addon) => selectedChoices.includes(addon.style_code))
    .reduce((sum, addon) => (sum += addon.surcharge), 0)
  const totalItemPrice = productPrice + boxPrice + addonPrice

  const prefStore = state.wizardUI.prefStore
  const wizardLoading = state.wizard.loading

  return {
    addons,
    adminMode: state.wizard.adminMode,
    header: state.wizard.header,
    lid: state.wizardUI.lid,
    loading: !prefStore || !!wizardLoading,
    prefStore: state.wizardUI.prefStore,
    selectedChoices,
    totalItemPrice,
    wizardLoading: state.wizard.loading,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    toggleAddon: (styleCode) => dispatch(toggleAddonChoice(styleCode)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProductAddons)
